import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../store';
import { hideSidePanel } from '../../../store/actions/sidePanelActions';
import { showModal, hideModal } from '../../../store/actions/modalActions';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import { removeSourceFromMatter } from '../../../store/actions/Matters/MatterActions';
import { PiTrashLight, PiXLight, PiParagraphLight, PiGlobeLight, PiArrowSquareOutLight, PiFileLight, PiBookOpenLight, PiScalesLight, PiClipboardTextLight, PiChatCenteredTextLight } from 'react-icons/pi';
import LogoSpinner from '../LogoSpinner';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import Skeleton from 'react-loading-skeleton';
import { getUserType } from '../../../utils/authUtils';
import Button from './Button';
import FileTypeSkeleton from '../../Files/documents/FileTypeSkeleton';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, sourceReaderTourSteps } from '../FeatureTour';

interface CustomFileReaderProps {
  citation: {
    title: string;
    name: string;
    cdn_url?: string;
    blob_url?: string;
    external_url?: string;
    relevant_sections?: string | string[];
    summary?: string;
    justification?: string;
    content?: string;
    type?: 'file' | 'law' | 'case' | 'document';
    size?: number;
    created_at?: string;
    uploaded_at?: string;
    lastModified?: string;
    source?: string;
    id?: string;
    source_id?: string;
    country?: string;
    [key: string]: any;
  };
  matterId: string;
  sourceType: 'file' | 'law' | 'case' | 'document';
  userAccessLevel?: 'edit' | 'admin' | 'view';
  onClose?: () => void;
  onDelete?: () => void;
}

// Strip data-file extensions (.json, .txt, .xml, .csv, .html, .htm) from external
// URLs so the user is directed to the source provider's web page, not a raw file.
const cleanExternalUrl = (url: string): string => {
  try {
    const u = new URL(url);
    u.pathname = u.pathname.replace(/\.(json|txt|xml|csv|html?)$/i, '');
    return u.toString();
  } catch {
    return url;
  }
};

const CustomFileReader: React.FC<CustomFileReaderProps> = ({ citation, matterId, sourceType, userAccessLevel, onClose, onDelete }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [documentHtml, setDocumentHtml] = useState<string>('');
  const [isConvertingWord, setIsConvertingWord] = useState(false);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const canEdit = userAccessLevel === 'edit' || userAccessLevel === 'admin';

  const containerRef = useRef<HTMLDivElement | null>(null);
  const pdfRef = useRef<HTMLDivElement | null>(null);
  const documentContentRef = useRef<HTMLDivElement | null>(null);

  const {
    isRunning: isTourRunning,
    handleJoyrideCallback: handleTourCallback,
  } = useFeatureTour({
    tourId: 'source-reader',
    steps: sourceReaderTourSteps,
    readyToShow: !isLoading,
    autoStartDelay: 600,
  });

  // Convert blob to object URL, keep string URLs as-is
  const fileSource = useMemo(() => {
    if (citation.blob instanceof Blob) {
      return URL.createObjectURL(citation.blob);
    }
    if (citation.blobObject instanceof Blob) {
      return URL.createObjectURL(citation.blobObject);
    }
    if (citation.blob_url) return citation.blob_url;
    if (citation.cdn_url) return citation.cdn_url;
    if (citation.external_url?.toLowerCase().endsWith('.pdf')) return citation.external_url;
    return null;
  }, [citation.blob, citation.blobObject, citation.blob_url, citation.cdn_url, citation.external_url]);

  const getDocumentType = () => {
    const contentType = citation.contentType || '';
    const url = fileSource || '';
    const blobPath = citation.blobPath || citation.blob_path || '';

    if (url.includes('.pdf') || contentType.includes('pdf') || blobPath.endsWith('.pdf')) return 'pdf';
    if (contentType.includes('word') || contentType.includes('document') || url.includes('.docx') || blobPath.endsWith('.docx')) return 'word';
    if (contentType.includes('sheet') || url.includes('.xlsx') || blobPath.endsWith('.xlsx')) return 'excel';
    if (contentType.includes('presentation') || url.includes('.pptx') || blobPath.endsWith('.pptx')) return 'powerpoint';
    if (contentType.includes('html') || url.includes('.html') || /\.html?$/i.test(blobPath)) return 'html';
    if (contentType.includes('json') || blobPath.endsWith('.json')) return 'json';
    if (contentType.includes('xml') || blobPath.endsWith('.xml')) return 'xml';
    if (contentType.includes('markdown') || blobPath.endsWith('.md')) return 'markdown';
    if (contentType.includes('text') || url.includes('.txt') || blobPath.endsWith('.txt')) return 'text';

    return 'unknown';
  };

  const documentType = getDocumentType();
  const isPdf = documentType === 'pdf';

  // Type-based colors and icons
  const getTypeConfig = () => {
    const type = citation.type || 'file';
    switch (type) {
      case 'law':
        return { 
          accent: 'blue', 
          icon: <PiScalesLight className="h-5 w-5 text-blue-600" />, 
          bg: 'from-blue-50 to-blue-100',
          badge: 'bg-blue-50 text-blue-700 ring-1 ring-blue-100'
        };
      case 'case':
        return { 
          accent: 'indigo', 
          icon: <PiBookOpenLight className="h-5 w-5 text-indigo-600" />, 
          bg: 'from-indigo-50 to-indigo-100', 
          badge: 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100'
        };
      default:
        return { 
          accent: 'gray', 
          icon: <PiFileLight className="h-5 w-5 text-gray-600" />, 
          bg: 'from-gray-50 to-gray-100',
          badge: 'bg-gray-50 text-gray-700 ring-1 ring-gray-200'
        };
    }
  };

  const typeConfig = getTypeConfig();
  const accentClass = `text-${typeConfig.accent}-600 dark:text-${typeConfig.accent}-400`;

  // Loading state is based on whether we have the blob/fileSource data from streaming
  // It's not based on iframe onLoad (that's handled separately)
  useEffect(() => {
    // Once we have fileSource, we're done loading the data
    // The iframe will handle its own rendering
    if (fileSource) {
      setIsLoading(false);
    }
  }, [fileSource]);

  // If citation has metadata (title/summary) but no blob/URL, streaming either failed or
  // is not applicable (e.g. laws/cases with only external_url). Stop showing the loader.
  // BUT: if a blob_path exists, streaming is expected — keep showing the skeleton until
  // the blob arrives or a longer timeout elapses (the stream can take 10+ seconds).
  useEffect(() => {
    const hasMetadata = citation.title || citation.name || citation.summary;
    const hasFileData = citation.blob || citation.blobObject || citation.blob_url || citation.cdn_url;
    const expectsStreaming = !!(citation.blob_path || citation.blobPath);
    if (hasMetadata && !hasFileData && !fileSource) {
      // If a blob_path exists, the backend is streaming — use a longer timeout
      const delay = expectsStreaming ? 30000 : 2000;
      const timeout = setTimeout(() => {
        setIsLoading(false);
      }, delay);
      return () => clearTimeout(timeout);
    }
  }, [citation.title, citation.name, citation.summary, citation.blob, citation.blobObject, citation.blob_url, citation.cdn_url, citation.blob_path, citation.blobPath, fileSource]);

  useEffect(() => {
    const country = citation.country?.toLowerCase();
    const docType = getDocumentType();
    // Skip if this is a JSON/XML/markdown doc — those have their own useEffects
    if (['json', 'xml', 'markdown'].includes(docType)) return;
    const isHtmlSource = country === 'no' || country === 'se' || docType === 'html';
    if (!isHtmlSource || !(citation.blob || citation.blobObject)) return;

    const blob = citation.blob || citation.blobObject;
    blob.text().then((text: string) => {
      if (country === 'no') {
        // Norwegian legal HTML parser (lovdata.no structure)
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const main = doc.querySelector('main#dokument');
        let contentHtml = main ? main.innerHTML : text;

        // Also extract metadata from the header if present
        const header = doc.querySelector('header.documentHeader');
        let metadataHtml = '';
        if (header) {
          const title = header.querySelector('dd.title')?.textContent || '';
          const shortTitle = header.querySelector('dd.titleShort')?.textContent || '';
          const ministry = header.querySelector('dd.ministry')?.textContent?.trim() || '';
          const dateInForce = header.querySelector('dd.dateInForce')?.textContent || '';
          const legacyId = header.querySelector('dd.legacyID')?.textContent || '';

          if (title || shortTitle || ministry || dateInForce || legacyId) {
            metadataHtml = `
              <div class="nr-metadata">
                ${shortTitle ? `<div class="nr-metadata-short-title">${shortTitle}</div>` : ''}
                ${legacyId ? `<div class="nr-metadata-id">${legacyId}</div>` : ''}
                <div class="nr-metadata-details">
                  ${ministry ? `<span>${ministry}</span>` : ''}
                  ${dateInForce ? `<span>I kraft: ${dateInForce}</span>` : ''}
                </div>
              </div>
            `;
          }
        }

        const style = `
          <style>
            /* ──────── RESET & BASE ──────── */
            .norway-reader * { font-family: inherit; box-sizing: border-box; }
            .norway-reader {
              font-size: 0.875rem;
              line-height: 1.7;
              color: #1a1a1a;
              max-width: 100%;
              padding: 0 0.5rem;
            }
            .dark .norway-reader { color: #e5e7eb; }

            /* ──────── METADATA BANNER ──────── */
            .norway-reader .nr-metadata {
              background: #fafafa;
              border: 1px solid #e5e5e5;
              border-radius: 6px;
              padding: 10px 14px;
              margin-bottom: 20px;
              font-size: 0.8rem;
            }
            .dark .norway-reader .nr-metadata {
              background: #1e1e1e;
              border-color: #333;
            }
            .norway-reader .nr-metadata-short-title {
              font-weight: 600;
              font-size: 0.85rem;
              color: #111;
              margin-bottom: 2px;
            }
            .dark .norway-reader .nr-metadata-short-title { color: #e0e0e0; }
            .norway-reader .nr-metadata-id {
              font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
              font-size: 0.75rem;
              color: #666;
              margin-bottom: 4px;
            }
            .dark .norway-reader .nr-metadata-id { color: #999; }
            .norway-reader .nr-metadata-details {
              display: flex;
              flex-wrap: wrap;
              gap: 12px;
              font-size: 0.75rem;
              color: #888;
            }
            .dark .norway-reader .nr-metadata-details { color: #777; }

            /* ──────── HEADINGS ──────── */
            .norway-reader h1 {
              font-size: 1.25rem;
              font-weight: 700;
              line-height: 1.3;
              color: #111;
              margin: 0 0 16px 0;
              padding-bottom: 10px;
              border-bottom: 1px solid #e0e0e0;
            }
            .dark .norway-reader h1 { color: #f3f4f6; border-bottom-color: #374151; }
            .norway-reader h2 {
              font-size: 1.05rem;
              font-weight: 600;
              color: #222;
              margin: 28px 0 12px 0;
              padding: 8px 12px;
              background: #f5f5f5;
              border-left: 3px solid #999;
              border-radius: 0 4px 4px 0;
            }
            .dark .norway-reader h2 {
              color: #d1d5db;
              background: #1f2937;
              border-left-color: #6b7280;
            }
            .norway-reader h3 {
              font-size: 0.95rem;
              font-weight: 600;
              color: #333;
              margin: 20px 0 8px 0;
            }
            .dark .norway-reader h3 { color: #d1d5db; }

            /* ──────── LEGAL ARTICLES (§) ──────── */
            .norway-reader .legalArticle {
              margin: 16px 0;
              padding: 0;
            }
            .norway-reader .legalArticleHeader {
              font-size: 0.9rem;
              font-weight: 600;
              color: #222;
              margin: 0 0 6px 0;
              padding: 6px 10px;
              background: #f8f8f8;
              border-radius: 4px;
              border: 1px solid #eee;
            }
            .dark .norway-reader .legalArticleHeader {
              color: #e5e7eb;
              background: #1f2937;
              border-color: #374151;
            }
            .norway-reader .legalArticleValue {
              color: #444;
              font-weight: 700;
            }
            .dark .norway-reader .legalArticleValue { color: #d1d5db; }
            .norway-reader .legalArticleTitle {
              font-weight: 500;
            }

            /* ──────── PARAGRAPHS ──────── */
            .norway-reader .legalP,
            .norway-reader article.legalP {
              display: block;
              margin: 6px 0;
              padding: 0 0 0 10px;
              line-height: 1.7;
            }
            .norway-reader .defaultP,
            .norway-reader article.defaultP {
              display: block;
              margin: 10px 0 4px 0;
              padding: 0 0 0 10px;
            }
            .norway-reader .defaultP i,
            .norway-reader article.defaultP i {
              font-style: italic;
              font-weight: 500;
              color: #555;
              font-size: 0.85rem;
            }
            .dark .norway-reader .defaultP i,
            .dark .norway-reader article.defaultP i { color: #9ca3af; }

            /* ──────── LISTS ──────── */
            .norway-reader ul.defaultList {
              list-style: none;
              margin: 6px 0 6px 10px;
              padding: 0;
            }
            .norway-reader ul.defaultList > li {
              position: relative;
              padding: 2px 0 2px 18px;
              margin: 2px 0;
            }
            .norway-reader ul.defaultList > li::before {
              content: "–";
              position: absolute;
              left: 0;
              color: #999;
              font-weight: 400;
            }
            .norway-reader ul.defaultList > li .listArticle {
              display: block;
            }
            .norway-reader ul.defaultList > li .listArticle .legalP {
              padding-left: 0;
              margin: 0;
            }

            /* ──────── TABLE OF CONTENTS ──────── */
            .norway-reader ul.tocTopUl,
            .norway-reader ul.tocSubUl {
              list-style: none;
              margin: 0;
              padding: 0;
            }
            .norway-reader ul.tocTopUl {
              background: #fafafa;
              border: 1px solid #e5e5e5;
              border-radius: 6px;
              padding: 14px;
              margin: 10px 0;
            }
            .dark .norway-reader ul.tocTopUl {
              background: #1f2937;
              border-color: #374151;
            }
            .norway-reader ul.tocTopUl > li {
              font-weight: 600;
              font-size: 0.85rem;
              color: #222;
            }
            .dark .norway-reader ul.tocTopUl > li { color: #e5e7eb; }
            .norway-reader ul.tocSubUl {
              margin-left: 16px;
              margin-top: 4px;
            }
            .norway-reader ul.tocSubUl > li {
              font-weight: 400;
              font-size: 0.8rem;
              color: #555;
              padding: 2px 0;
              cursor: pointer;
            }
            .dark .norway-reader ul.tocSubUl > li { color: #9ca3af; }
            .norway-reader ul.tocSubUl > li:hover {
              color: #111;
            }
            .dark .norway-reader ul.tocSubUl > li:hover { color: #e5e7eb; }

            /* ──────── TABLES ──────── */
            .norway-reader table {
              width: 100%;
              border-collapse: collapse;
              margin: 10px 0;
              font-size: 0.8rem;
              border: 1px solid #e0e0e0;
              border-radius: 6px;
              overflow: hidden;
            }
            .dark .norway-reader table { border-color: #374151; }
            .norway-reader thead {
              background: #f5f5f5;
            }
            .dark .norway-reader thead { background: #1f2937; }
            .norway-reader thead tr:first-child th[colspan] {
              background: #eee;
              font-size: 0.85rem;
              font-weight: 600;
              color: #333;
              padding: 8px 12px;
              text-align: left;
            }
            .dark .norway-reader thead tr:first-child th[colspan] {
              background: #1f2937;
              color: #d1d5db;
            }
            .norway-reader th {
              padding: 8px 12px;
              text-align: left;
              font-weight: 600;
              font-size: 0.78rem;
              color: #333;
              border-bottom: 1px solid #ddd;
            }
            .dark .norway-reader th { color: #d1d5db; border-bottom-color: #4b5563; }
            .norway-reader td {
              padding: 6px 12px;
              border-bottom: 1px solid #f0f0f0;
              color: #333;
              vertical-align: top;
            }
            .dark .norway-reader td { border-bottom-color: #374151; color: #d1d5db; }
            .norway-reader tbody tr:hover {
              background: #fafafa;
            }
            .dark .norway-reader tbody tr:hover { background: #1f2937; }
            .norway-reader tbody tr.startGroup td {
              border-top: 1px solid #ddd;
              padding-top: 10px;
            }
            .dark .norway-reader tbody tr.startGroup td { border-top-color: #4b5563; }
            .norway-reader td i,
            .norway-reader th i {
              font-style: italic;
            }

            /* ──────── FOOTNOTES ──────── */
            .norway-reader footer.footnotes {
              margin-top: 16px;
              padding-top: 12px;
              border-top: 1px solid #e0e0e0;
            }
            .dark .norway-reader footer.footnotes { border-top-color: #374151; }
            .norway-reader .footnote {
              display: block;
              font-size: 0.8rem;
              color: #777;
              margin: 4px 0;
              padding-left: 12px;
            }
            .dark .norway-reader .footnote { color: #9ca3af; }
            .norway-reader .footnoteLabel {
              font-weight: 700;
              color: #555;
              margin-right: 4px;
            }
            .dark .norway-reader .footnoteLabel { color: #d1d5db; }
            .norway-reader sup.footnotereference {
              font-size: 0.65rem;
              color: #555;
              cursor: help;
              font-weight: 600;
            }
            .dark .norway-reader sup.footnotereference { color: #9ca3af; }

            /* ──────── SECTIONS ──────── */
            .norway-reader section.section {
              margin: 24px 0;
              padding: 0;
            }

            /* ──────── DEFINITION LISTS (metadata) ──────── */
            .norway-reader dl {
              margin: 8px 0;
            }
            .norway-reader dt {
              font-weight: 600;
              color: #888;
              font-size: 0.75rem;
              text-transform: uppercase;
              letter-spacing: 0.05em;
              margin-top: 8px;
            }
            .dark .norway-reader dt { color: #9ca3af; }
            .norway-reader dd {
              margin: 2px 0 8px 0;
              color: #222;
            }
            .dark .norway-reader dd { color: #e5e7eb; }

            /* ──────── LINKS ──────── */
            .norway-reader a {
              color: #555;
              text-decoration: underline;
              text-underline-offset: 2px;
              text-decoration-color: #ccc;
              transition: color 0.15s, text-decoration-color 0.15s;
            }
            .norway-reader a:hover {
              color: #111;
              text-decoration-color: #666;
            }
            .dark .norway-reader a { color: #9ca3af; text-decoration-color: #555; }
            .dark .norway-reader a:hover { color: #e5e7eb; text-decoration-color: #999; }

            /* ──────── MISC ──────── */
            .norway-reader strong { font-weight: 600; }
            .norway-reader i { font-style: italic; }
            .norway-reader .data-document-key-info { display: none; }

            /* ──────── SCROLL TARGET HIGHLIGHT ──────── */
            .norway-reader .nr-scroll-highlight {
              animation: nr-highlight 2s ease-out;
            }
            @keyframes nr-highlight {
              0% { background-color: #f0f0f0; outline: 2px solid #ccc; }
              100% { background-color: transparent; outline: 2px solid transparent; }
            }
            .dark .norway-reader .nr-scroll-highlight {
              animation: nr-highlight-dark 2s ease-out;
            }
            @keyframes nr-highlight-dark {
              0% { background-color: #374151; outline: 2px solid #4b5563; }
              100% { background-color: transparent; outline: 2px solid transparent; }
            }
          </style>
        `;
        setDocumentHtml(style + '<div class="norway-reader">' + metadataHtml + contentHtml + '</div>');
      } else {
        // Swedish / generic legal HTML parser
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');

        // Remove all script tags for security
        doc.querySelectorAll('script').forEach(el => el.remove());
        doc.querySelectorAll('style').forEach(el => el.remove());
        // Remove search bars, navigation, headers, footers, and other non-content elements
        doc.querySelectorAll('form, nav, header, footer, [role="search"], [role="navigation"], .search, .navbar, .nav, .header, .footer, .menu, .sidebar, input, button, select, textarea').forEach(el => el.remove());

        // Try to find main content area (lagen.nu uses body directly)
        const body = doc.body;
        let contentHtml = body ? body.innerHTML : text;

        const style = `
          <style>
            .se-reader * { font-family: inherit; box-sizing: border-box; }
            .se-reader {
              font-size: 0.875rem;
              line-height: 1.7;
              color: #1a1a1a;
              max-width: 100%;
              padding: 0 0.5rem;
            }
            .dark .se-reader { color: #e5e7eb; }
            .se-reader h1 {
              font-size: 1.25rem; font-weight: 700; line-height: 1.3; color: #111;
              margin: 0 0 16px 0; padding-bottom: 10px;
              border-bottom: 1px solid #e0e0e0;
            }
            .dark .se-reader h1 { color: #f3f4f6; border-bottom-color: #374151; }
            .se-reader h2 {
              font-size: 1.05rem; font-weight: 600; color: #222;
              margin: 28px 0 12px 0; padding: 8px 12px;
              background: #f5f5f5; border-left: 3px solid #3b82f6; border-radius: 0 4px 4px 0;
            }
            .dark .se-reader h2 { color: #d1d5db; background: #1f2937; border-left-color: #60a5fa; }
            .se-reader h3 {
              font-size: 0.95rem; font-weight: 600; color: #333; margin: 20px 0 8px 0;
            }
            .dark .se-reader h3 { color: #d1d5db; }
            .se-reader p { margin: 6px 0; line-height: 1.7; }
            .se-reader a {
              color: #555; text-decoration: underline;
              text-underline-offset: 2px; text-decoration-color: #ccc;
            }
            .se-reader a:hover { color: #111; text-decoration-color: #666; }
            .dark .se-reader a { color: #9ca3af; text-decoration-color: #555; }
            .dark .se-reader a:hover { color: #e5e7eb; }
            .se-reader table {
              width: 100%; border-collapse: collapse; margin: 10px 0;
              font-size: 0.8rem; border: 1px solid #e0e0e0;
            }
            .dark .se-reader table { border-color: #374151; }
            .se-reader th {
              padding: 8px 12px; text-align: left; font-weight: 600;
              font-size: 0.78rem; color: #333; border-bottom: 1px solid #ddd;
              background: #f5f5f5;
            }
            .dark .se-reader th { color: #d1d5db; border-bottom-color: #4b5563; background: #1f2937; }
            .se-reader td {
              padding: 6px 12px; border-bottom: 1px solid #f0f0f0;
              color: #333; vertical-align: top;
            }
            .dark .se-reader td { border-bottom-color: #374151; color: #d1d5db; }
            .se-reader ul, .se-reader ol { margin: 6px 0 6px 20px; padding: 0; }
            .se-reader li { margin: 4px 0; }
            .se-reader dl { margin: 8px 0; }
            .se-reader dt { font-weight: 600; color: #555; margin-top: 8px; }
            .dark .se-reader dt { color: #9ca3af; }
            .se-reader dd { margin: 2px 0 8px 0; color: #222; }
            .dark .se-reader dd { color: #e5e7eb; }
            .se-reader strong { font-weight: 600; }
            .se-reader i, .se-reader em { font-style: italic; }
            .se-reader img { display: none; }
          </style>
        `;
        setDocumentHtml(style + '<div class="se-reader">' + contentHtml + '</div>');
      }
    }).catch((error: any) => {
        console.error('Error parsing document:', error);
        setDocumentHtml('');
    });
  }, [citation.blob, citation.blobObject, citation.country]);

  // Handle JSON documents — render structured legal data as readable HTML
  useEffect(() => {
    const isJsonDoc = documentType === 'json' && (citation.blob || citation.blobObject);
    if (!isJsonDoc) return;

    const blob = citation.blob || citation.blobObject;
    blob.text().then((text: string) => {
      try {
        const data = JSON.parse(text);

        // Build readable HTML from the JSON legal document
        const escapeHtml = (str: string) =>
          String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

        // Check if a string contains HTML tags (e.g. <p>, <br>, <strong>)
        const containsHtml = (str: string) => /<[a-z][a-z0-9]*[\s>\/]/i.test(str);

        // Sanitize HTML — allow only safe tags, strip everything else
        const sanitizeHtml = (str: string) => {
          const allowedTags = /^(p|br|strong|b|em|i|u|ul|ol|li|h[1-6]|a|span|div|table|tr|td|th|thead|tbody|blockquote|pre|code|hr|sub|sup|dl|dt|dd)$/i;
          return str.replace(/<\/?([a-z][a-z0-9]*)\b[^>]*>/gi, (match, tag) => {
            return allowedTags.test(tag) ? match : '';
          }).replace(/<script[\s\S]*?<\/script>/gi, '');
        };

        // Render a string value — if it contains HTML, sanitize and render it; otherwise escape
        const renderString = (str: string) =>
          containsHtml(str) ? sanitizeHtml(str) : escapeHtml(str);

        const renderValue = (value: any, depth: number = 0): string => {
          if (value === null || value === undefined) return '';
          if (Array.isArray(value)) {
            if (value.length === 0) return '';
            // Check if array of strings
            if (value.every((v: any) => typeof v === 'string')) {
              return '<ul class="json-list">' + value.map((v: any) => `<li>${renderString(v)}</li>`).join('') + '</ul>';
            }
            // Array of objects
            return value.map((v: any) => renderValue(v, depth + 1)).join('');
          }
          if (typeof value === 'object') {
            return renderObject(value, depth + 1);
          }
          return renderString(String(value));
        };

        const LABEL_MAP: Record<string, string> = {
          'sammanfattning': 'Summary', 'innehall': 'Content', 'rubrik': 'Title',
          'domstolKod': 'Court Code', 'domstolNamn': 'Court', 'malNummerLista': 'Case Numbers',
          'lagrumLista': 'Legal References', 'litteraturLista': 'Literature',
          'nyckelordLista': 'Keywords', 'publiceringstid': 'Published', 'typ': 'Type',
          'referatNummerLista': 'Report Numbers', 'rattsmradeLista': 'Legal Areas',
          'arVagledande': 'Is Guiding', 'arbetsdomstolenDomsnummer': 'AD Case No.',
          'bilagaLista': 'Attachments', 'forarbeteLista': 'Preparatory Works',
          'gruppKorrelationsnummer': 'Correlation ID', 'hanvisadePubliceringarLista': 'Related Refs',
          'blob_path': '', 'id': '',
        };

        const renderObject = (obj: any, depth: number = 0): string => {
          let html = '';
          for (const [key, val] of Object.entries(obj)) {
            if (val === null || val === undefined || val === '') continue;
            if (Array.isArray(val) && val.length === 0) continue;
            const label = LABEL_MAP[key] !== undefined ? LABEL_MAP[key] : key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
            if (label === '') continue; // skip hidden fields

            const rendered = renderValue(val, depth);
            if (!rendered) continue;

            // Large text fields get their own section
            if (typeof val === 'string' && val.length > 200) {
              html += `<div class="json-section"><h3 class="json-label">${escapeHtml(label)}</h3><div class="json-content json-long-text">${renderString(val)}</div></div>`;
            } else if (typeof val === 'object') {
              html += `<div class="json-section"><h3 class="json-label">${escapeHtml(label)}</h3><div class="json-content">${rendered}</div></div>`;
            } else {
              html += `<div class="json-field"><span class="json-field-label">${escapeHtml(label)}</span><span class="json-field-value">${renderString(String(val))}</span></div>`;
            }
          }
          return html;
        };

        const contentHtml = renderObject(data);

        const style = `<style>
          .json-reader * { font-family: inherit; box-sizing: border-box; }
          .json-reader { font-size: 0.875rem; line-height: 1.7; color: #1a1a1a; max-width: 100%; padding: 0.5rem; }
          .dark .json-reader { color: #e5e7eb; }
          .json-reader .json-section { margin: 16px 0; }
          .json-reader .json-label {
            font-size: 0.8rem; font-weight: 600; color: #555; text-transform: uppercase;
            letter-spacing: 0.05em; margin: 0 0 6px 0; padding: 6px 10px;
            background: #f5f5f5; border-left: 3px solid #999; border-radius: 0 4px 4px 0;
          }
          .dark .json-reader .json-label { color: #9ca3af; background: #1f2937; border-left-color: #6b7280; }
          .json-reader .json-content { padding: 0 0 0 12px; }
          .json-reader .json-long-text { white-space: pre-wrap; line-height: 1.8; color: #333; }
          .dark .json-reader .json-long-text { color: #d1d5db; }
          .json-reader .json-field {
            display: flex; gap: 12px; padding: 6px 10px; border-bottom: 1px solid #f0f0f0;
            align-items: baseline;
          }
          .dark .json-reader .json-field { border-bottom-color: #374151; }
          .json-reader .json-field-label { font-weight: 600; color: #555; min-width: 140px; flex-shrink: 0; font-size: 0.8rem; }
          .dark .json-reader .json-field-label { color: #9ca3af; }
          .json-reader .json-field-value { color: #222; word-break: break-word; }
          .dark .json-reader .json-field-value { color: #e5e7eb; }
          .json-reader .json-list { list-style: none; margin: 4px 0; padding: 0; }
          .json-reader .json-list li { position: relative; padding: 2px 0 2px 16px; margin: 2px 0; }
          .json-reader .json-list li::before { content: "–"; position: absolute; left: 0; color: #999; }
        </style>`;
        setDocumentHtml(style + '<div class="json-reader">' + contentHtml + '</div>');
      } catch (e) {
        // Not valid JSON — show as plain text
        const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        setDocumentHtml(`<pre style="white-space:pre-wrap;font-size:0.85rem;padding:1rem;line-height:1.6;color:#333;">${escaped}</pre>`);
      }
    }).catch(() => setDocumentHtml(''));
  }, [citation.blob, citation.blobObject, documentType]);

  // Handle XML documents — render as styled readable content
  useEffect(() => {
    const isXmlDoc = documentType === 'xml' && (citation.blob || citation.blobObject);
    if (!isXmlDoc) return;

    const blob = citation.blob || citation.blobObject;
    blob.text().then((text: string) => {
      // Try to parse as HTML-like XML and show content
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'application/xml');
      const parseError = doc.querySelector('parsererror');
      if (parseError) {
        // Not valid XML — show as formatted text
        const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        setDocumentHtml(`<pre style="white-space:pre-wrap;font-size:0.85rem;padding:1rem;line-height:1.6;color:#333;font-family:ui-monospace,monospace;">${escaped}</pre>`);
      } else {
        // Valid XML — render as indented, syntax-highlighted text
        const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const highlighted = escaped
          .replace(/&lt;(\/?[\w:.-]+)/g, '&lt;<span style="color:#2563eb;">$1</span>')
          .replace(/([\w:.-]+)=&quot;/g, '<span style="color:#059669;">$1</span>=&quot;')
          .replace(/([\w:.-]+)="/g, '<span style="color:#059669;">$1</span>="');
        setDocumentHtml(`<pre style="white-space:pre-wrap;font-size:0.8rem;padding:1rem;line-height:1.5;color:#333;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;">${highlighted}</pre>`);
      }
    }).catch(() => setDocumentHtml(''));
  }, [citation.blob, citation.blobObject, documentType]);

  // Handle Markdown documents — render as styled HTML
  useEffect(() => {
    const isMdDoc = documentType === 'markdown' && (citation.blob || citation.blobObject);
    if (!isMdDoc) return;

    const blob = citation.blob || citation.blobObject;
    blob.text().then((text: string) => {
      // Simple markdown-to-HTML conversion for common patterns
      let html = text
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        // Headers
        .replace(/^### (.+)$/gm, '<h3>$1</h3>')
        .replace(/^## (.+)$/gm, '<h2>$1</h2>')
        .replace(/^# (.+)$/gm, '<h1>$1</h1>')
        // Bold / italic
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        // Code blocks
        .replace(/```[\w]*\n([\s\S]*?)```/g, '<pre style="background:#f5f5f5;padding:12px;border-radius:4px;overflow-x:auto;font-size:0.8rem;">$1</pre>')
        // Inline code
        .replace(/`([^`]+)`/g, '<code style="background:#f0f0f0;padding:2px 4px;border-radius:3px;font-size:0.85em;">$1</code>')
        // List items
        .replace(/^- (.+)$/gm, '<li>$1</li>')
        .replace(/^(\d+)\. (.+)$/gm, '<li>$2</li>')
        // Horizontal rules
        .replace(/^---+$/gm, '<hr style="border:none;border-top:1px solid #e0e0e0;margin:16px 0;">')
        // Paragraphs (double newlines)
        .replace(/\n\n/g, '</p><p>')
        // Single newlines
        .replace(/\n/g, '<br>');

      const style = `<style>
        .md-reader { font-size:0.875rem; line-height:1.7; color:#1a1a1a; padding:0.5rem; }
        .dark .md-reader { color:#e5e7eb; }
        .md-reader h1 { font-size:1.25rem; font-weight:700; margin:0 0 12px; padding-bottom:8px; border-bottom:1px solid #e0e0e0; }
        .md-reader h2 { font-size:1.05rem; font-weight:600; margin:20px 0 8px; }
        .md-reader h3 { font-size:0.95rem; font-weight:600; margin:16px 0 6px; }
        .md-reader p { margin:6px 0; }
        .md-reader li { margin:4px 0 4px 20px; }
        .dark .md-reader h1 { border-bottom-color:#374151; }
      </style>`;
      setDocumentHtml(style + '<div class="md-reader"><p>' + html + '</p></div>');
    }).catch(() => setDocumentHtml(''));
  }, [citation.blob, citation.blobObject, documentType]);

  // Handle Word documents with mammoth.js
  useEffect(() => {
    const isWordDocument = documentType === 'word' && (citation.blob || citation.blobObject);
    if (!isWordDocument) return;

    const convertWordDocument = async () => {
      setIsConvertingWord(true);
      try {
        const blob = citation.blob || citation.blobObject;
        const arrayBuffer = await blob.arrayBuffer();
        
        const mammoth = await import('mammoth');
        const result = await mammoth.convertToHtml(
          { arrayBuffer },
          {
            convertImage: mammoth.images.imgElement(function(image: any) {
              return image.read("base64").then(function(imageBuffer: string) {
                return { src: `data:${image.contentType};base64,${imageBuffer}` };
              });
            }),
            styleMap: [
              "p[style-name='Heading 1'] => h1.doc-heading:fresh",
              "p[style-name='Heading 2'] => h2.doc-heading:fresh",
              "p[style-name='Heading 3'] => h3.doc-heading:fresh",
              "p[style-name='Title'] => h1.doc-title:fresh",
              "b => strong",
              "i => em",
              "u => u"
            ]
          }
        );
        
        const styledHtml = `
          <style>
            .word-reader { font-family: "Times New Roman", Times, serif; font-size: 10pt; line-height: 1.4; color: #1a1a1a; padding-left: 20px; padding-right: 20px; }
            .word-reader img { max-width: 100%; height: auto; margin: 0.75em 0; }
            .word-reader table { border-collapse: collapse; width: 100%; margin: 0.75em 0; }
            .word-reader td, .word-reader th { border: 1px solid #ddd; padding: 6px; font-size: 10pt; font-weight: normal; }
            .word-reader h1, .word-reader h2, .word-reader h3, .word-reader h4, .word-reader h5, .word-reader h6 { color: #1a5490; }
            .word-reader h1, .word-reader h1.doc-heading { font-size: 18pt; margin-top: 0; margin-bottom: 10pt; font-weight: bold; }
            .word-reader h2, .word-reader h2.doc-heading { font-size: 14pt; margin-top: 14pt; margin-bottom: 5pt; font-weight: bold; }
            .word-reader h3, .word-reader h3.doc-heading { font-size: 12pt; margin-top: 12pt; margin-bottom: 4pt; font-weight: bold; }
            .word-reader p { margin: 0 0 10pt 0; }
            .word-reader ul, .word-reader ol { margin: 0 0 10pt 0; padding-left: 20pt; }
          </style>
          <div class="word-reader">${result.value}</div>
        `;
        
        setDocumentHtml(styledHtml);
        setIsLoading(false);
        setIsConvertingWord(false);
      } catch (error) {
        console.error('Error converting Word document:', error);
        setDocumentHtml('');
        setIsLoading(false);
        setIsConvertingWord(false);
      }
    };

    convertWordDocument();
  }, [citation.blob, citation.blobObject, documentType]);


  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  // Scroll to a section in the document viewer by searching for matching text
  const scrollToSection = (sectionText: string) => {
    const container = documentContentRef.current;
    if (!container) return;

    const normalizedSearch = sectionText.trim().toLowerCase();
    let targetEl: Element | null = null;

    // Helper: extract all § numbers from a section reference like "§§3–8" or "§3 og §4" or "§ 1"
    const extractParagrafNumbers = (text: string): string[] => {
      const nums: string[] = [];
      // Match §§X–Y or §§X-Y (range)
      const rangeMatch = text.match(/§§\s*(\d+)[–\-](\d+)/);
      if (rangeMatch) {
        const start = parseInt(rangeMatch[1]);
        const end = parseInt(rangeMatch[2]);
        for (let i = start; i <= end; i++) nums.push(String(i));
        return nums;
      }
      // Match individual § references: §3, § 3, §§3, etc.
      const individualMatches = text.matchAll(/§\s*(\d+)/g);
      for (const m of individualMatches) {
        if (!nums.includes(m[1])) nums.push(m[1]);
      }
      return nums;
    };

    // Helper: extract Kapittel number/roman numeral
    const extractKapittel = (text: string): string | null => {
      const match = text.match(/kapittel\s+([\divxlcm]+)/i);
      return match ? match[1].toLowerCase() : null;
    };

    // Strategy 1: Try to find § in the document
    const paragrafNums = extractParagrafNumbers(normalizedSearch);
    if (paragrafNums.length > 0) {
      const firstNum = paragrafNums[0];
      // Search for the § header element: look for .legalArticleValue containing "§ X"
      const allValues = container.querySelectorAll('.legalArticleValue, .legalArticleHeader');
      for (const el of allValues) {
        const elText = (el.textContent || '').replace(/\s+/g, ' ').trim();
        // Match "§ 3" or "§3" patterns
        if (elText.match(new RegExp(`§\\s*${firstNum}\\b`))) {
          targetEl = el.closest('.legalArticle') || el.closest('h3') || el;
          break;
        }
      }
      // Also try data-name attributes
      if (!targetEl) {
        targetEl = container.querySelector(`[data-name="§${firstNum}"]`) ||
                   container.querySelector(`[data-name="§ ${firstNum}"]`) ||
                   container.querySelector(`article[data-name*="§${firstNum}"]`);
      }
    }

    // Strategy 2: Try Kapittel match
    if (!targetEl) {
      const kap = extractKapittel(normalizedSearch);
      if (kap) {
        // Try roman numeral or number in headings
        const headings = container.querySelectorAll('h1, h2, h3, section[id], [data-name]');
        for (const el of headings) {
          const elText = (el.textContent || '').toLowerCase();
          if (elText.match(new RegExp(`kapittel\\s+${kap}\\b`, 'i'))) {
            targetEl = el;
            break;
          }
        }
        // Also try id-based
        if (!targetEl) {
          targetEl = container.querySelector(`#kapittel-${kap}`) ||
                     container.querySelector(`[data-name="kap${kap}"]`) ||
                     container.querySelector(`[id*="kapittel"]`);
        }
      }
    }

    // Strategy 3: Direct text match on headings and structural elements
    if (!targetEl) {
      // Try to extract a meaningful phrase - strip law references like "i voldsoffererstatningsloven"
      let searchPhrase = normalizedSearch
        .replace(/\s+i\s+[a-zæøå\-]+loven.*$/i, '')
        .replace(/\s+i\s+(lov|for|forskrift)\/[\d\-]+.*$/i, '')
        .trim();

      const allHeaders = container.querySelectorAll('h1, h2, h3, .legalArticleHeader, .legalArticleTitle, section > *:first-child');
      for (const el of allHeaders) {
        const elText = (el.textContent || '').trim().toLowerCase();
        if (elText.includes(searchPhrase) || searchPhrase.includes(elText)) {
          targetEl = el;
          break;
        }
      }
    }

    // Strategy 4: Text node search - walk the DOM to find matching text
    if (!targetEl) {
      const walker = document.createTreeWalker(
        container,
        NodeFilter.SHOW_TEXT,
        null
      );
      let node: Node | null;
      // Extract key words from the search (remove common Norwegian prepositions)
      const keywords = normalizedSearch
        .replace(/[()§,]/g, ' ')
        .split(/\s+/)
        .filter(w => w.length > 2 && !['og','i','av','for','til','fra','med','den','det','de','en','et','som','på'].includes(w));

      if (keywords.length > 0) {
        while ((node = walker.nextNode())) {
          const nodeText = (node.textContent || '').toLowerCase();
          const matchCount = keywords.filter(kw => nodeText.includes(kw)).length;
          if (matchCount >= Math.ceil(keywords.length * 0.6)) {
            // Found a text node with good keyword overlap
            targetEl = node.parentElement;
            break;
          }
        }
      }
    }

    if (targetEl) {
      targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Highlight briefly with CSS class
      const el = targetEl as HTMLElement;
      el.classList.add('nr-scroll-highlight');
      setTimeout(() => {
        el.classList.remove('nr-scroll-highlight');
      }, 2200);
    }
  };

  const ContentSection: React.FC<{
    title: string;
    content: string | string[];
    icon: React.ReactNode;
    className?: string;
    isSectionList?: boolean;
  }> = ({ title, content, icon, className = '', isSectionList = false }) => {
    const isArray = Array.isArray(content);

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className={`space-y-3 ${className}`}
      >
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-full ${typeConfig.badge}`}>
            {icon}
          </div>
          <h4 className={`text-sm font-semibold ${accentClass} flex-1`}>{title}</h4>
          {isArray && <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 font-medium">{content.length}</span>}
        </div>

        {isSectionList && isArray ? (
          <div className="flex flex-wrap gap-2">
            {(content as string[]).map((section: string, idx: number) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.15, delay: idx * 0.04 }}
                className={`inline-flex items-center px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 transition-all duration-150 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer group`}
                onClick={() => scrollToSection(section)}
                title={t('Click to scroll to this section')}
              >
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 line-clamp-2">
                  {section}
                </span>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className={`p-3 rounded-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border ${typeConfig.badge.replace('ring-1', 'border')} overflow-y-auto prose prose-sm max-w-none`}>
            <div className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap text-sm">
              {isArray ? (
                <ul className="space-y-2 list-none">
                  {(content as string[]).map((item: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                content
              )}
            </div>
          </div>
        )}
      </motion.div>
    );
  };

  const handleDeleteSource = () => {
    dispatch(showModal({
      showModal: true,
      type: 'confirmation',
      message: t('Delete Source'),
      subMsg: t('This source will be permanently removed. This action cannot be undone.'),
      onConfirm: async () => {
        try {
          const sourceId = citation.source_id || citation.id;
          await dispatch(removeSourceFromMatter(matterId, sourceType, sourceId as string));
          dispatch(showFeedbackModal({
            showModal: true,
            message: t('Source deleted successfully'),
            modalType: 'success',
            duration: 3000
          }));
          if (onDelete) {
            onDelete();
          }
          dispatch(hideSidePanel());
        } catch (error) {
          console.error('Failed to delete source:', error);
          dispatch(showFeedbackModal({
            showModal: true,
            message: t('Failed to delete source'),
            modalType: 'error',
            duration: 3000
          }));
        }
        dispatch(hideModal());
      },
      onCancel: () => dispatch(hideModal()),
      icon: <PiTrashLight className="w-6 h-6 text-red-600" />,
      isDest: true,
    }));
  };

  // Show loading state while streaming file from backend
  const isStreamingData = !fileSource || (!citation.blob && !citation.blobObject);

  // Determine if the left sidebar has any meaningful content to show
  const hasSidebarContent = !!(
    citation.created_at || citation.lastModified || citation.source ||
    citation.size || citation.id || citation.relevant_sections ||
    citation.summary || citation.justification || citation.practical_application ||
    citation.external_url || canEdit
  );

  if (isLoading && isStreamingData) {
    return (
      <div className="flex flex-col h-full bg-white dark:bg-gray-900">
        {/* Compact top bar for loading state */}
        <div className="px-4 py-3 border-b border-gray-200/50 dark:border-gray-700/50 flex items-center gap-3">
          {onClose && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 transition-colors"
              onClick={onClose}
              title={t('Close')}
            >
              <PiXLight className="h-4 w-4" />
            </motion.button>
          )}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className={`p-1.5 rounded-full ${typeConfig.badge}`}>
              {typeConfig.icon}
            </div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
              {citation.title || citation.name || t('Loading document...')}
            </span>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <FileTypeSkeleton fileType={documentType as 'docx' | 'pdf' | 'excel' | 'text' | 'unknown'} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 overflow-hidden" ref={containerRef}>
      {/* Content area – sidebar + document OR full-width document */}
      <div className="flex flex-1 overflow-hidden">
      {hasSidebarContent && (
      <div data-tour-id="reader-sidebar" className="w-[45%] border-r border-gray-200/50 dark:border-gray-700/50 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm flex flex-col">

        <div className="space-y-4 p-4 flex-1 overflow-y-auto custom-scrollbar-main">
          {!(citation.created_at || citation.lastModified || citation.source || citation.size || citation.id) && isLoading ? (
            <div className="pb-4 border-b border-gray-200/50 dark:border-gray-700/50 space-y-3">
              <div className="flex items-center gap-2">
                <Skeleton circle width={28} height={28} />
                <Skeleton height={16} width="40%" />
              </div>
              <div className="space-y-2 bg-gray-50/50 dark:bg-gray-800/50 p-3 rounded-lg">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex justify-between">
                    <Skeleton height={12} width="30%" />
                    <Skeleton height={12} width="40%" />
                  </div>
                ))}
              </div>
            </div>
          ) : (citation.created_at || citation.lastModified || citation.source || citation.size || citation.id) ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="pb-4 border-b border-gray-200/50 dark:border-gray-700/50 space-y-3"
            >
              <div className="flex items-center gap-2">
                <div className={`p-1.5 rounded-full ${typeConfig.badge}`}>
                  <InformationCircleIcon className={`h-4 w-4 ${accentClass}`} />
                </div>
                <h4 className={`text-sm font-semibold ${accentClass}`}>{t('Document Info')}</h4>
              </div>
              <div className="space-y-2 text-xs bg-gray-50/50 dark:bg-gray-800/50 p-3 rounded-lg">
                {(citation.created_at || citation.uploaded_at) && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      {citation.uploaded_at ? t('Uploaded') : t('Created')}
                    </span>
                    <span className="text-gray-700 dark:text-gray-300 font-medium">
                      {formatDate(citation.uploaded_at || citation.created_at)}
                    </span>
                  </div>
                )}
                {citation.lastModified && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">{t('Last Modified')}</span>
                    <span className="text-gray-700 dark:text-gray-300 font-medium">
                      {formatDate(citation.lastModified)}
                    </span>
                  </div>
                )}
                {citation.size && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">{t('Size')}</span>
                    <span className="text-gray-700 dark:text-gray-300 font-medium">
                      {citation.size > 1024 * 1024
                        ? (citation.size / (1024 * 1024)).toFixed(2) + ' MB'
                        : (citation.size / 1024).toFixed(2) + ' KB'}
                    </span>
                  </div>
                )}
                {citation.source && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">{t('Source')}</span>
                    <span className="text-gray-700 dark:text-gray-300 font-medium truncate max-w-[120px]">
                      {citation.source}
                    </span>
                  </div>
                )}
              </div>
            </motion.div>
          ) : null}
        {(citation.relevant_sections) && (
            <ContentSection
              title={t('Relevant Sections')}
              content={
                Array.isArray(citation.relevant_sections)
                  ? citation.relevant_sections.map((s: any) =>
                      typeof s === 'string' ? s : (s.section_name ? `${s.section_name}: ${s.section_content || ''}` : s.section_content || String(s))
                    )
                  : typeof citation.relevant_sections === 'string'
                    ? [citation.relevant_sections]
                    : []
              }
              icon={<PiParagraphLight className="h-4 w-4" />}
              isSectionList={true}
            />
          )}
          
      {citation.summary && (
          <ContentSection
            title={t('Summary')}
            content={citation.summary}
            icon={<PiClipboardTextLight className="h-4 w-4" />}
          />
        )}

        {citation.justification && (
          <ContentSection
            title={t('Justification')}
            content={citation.justification}
            icon={<PiChatCenteredTextLight className="h-4 w-4" />}  
          />
        )}

        {citation.practical_application && (
          <ContentSection
            title={t('Practical Application')}
            content={citation.practical_application}
            icon={<PiChatCenteredTextLight className="h-4 w-4" />}  
          />
        )}

          {citation.external_url && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="pt-2"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className={`p-1.5 rounded-full ${typeConfig.badge}`}>
                  <PiGlobeLight className={`h-4 w-4 ${accentClass}`} />
                </div>
                <h4 className={`text-sm font-semibold ${accentClass}`}>{t('Source Link')}</h4>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-500 dark:text-gray-400 truncate flex-1" title={citation.external_url}>
                  {new URL(citation.external_url).hostname}
                </span>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`p-2 rounded-lg ${typeConfig.badge.replace('ring-1', 'bg hover:bg')} hover:text-${typeConfig.accent}-700 transition-colors text-xs`}
                  onClick={() => window.open(cleanExternalUrl(citation.external_url!), '_blank')}
                  title={t('Open Original')}
                >
                  <PiArrowSquareOutLight className="h-3 w-3" />
                </motion.button>
              </div>
            </motion.div>
          )}
        </div>
        <div data-tour-id="reader-actions" className="p-4 border-t border-gray-200/50 dark:border-gray-700/50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm flex items-center justify-between gap-4 sticky bottom-0">
          {canEdit && (
            <Button
              variant="destructive"
              size="small"
              onClick={handleDeleteSource}
            >
              {t('Delete Source')}
            </Button>
          )}
        </div>
      </div>
      )}

      <div data-tour-id="reader-content" className={`${hasSidebarContent ? 'w-[60%]' : 'w-full'} relative bg-white dark:bg-gray-900 overflow-hidden`}>
        <div className="flex-1 overflow-auto custom-scrollbar-main" ref={pdfRef}>
          <AnimatePresence mode="wait">
            {fileSource ? (
              <motion.div
                key="document"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="h-[calc(100vh-110px)] flex flex-col custom-scrollbar-main"
              >
                <div className="flex-1 h-[calc(100vh-80px)]">
                  {isConvertingWord ? (
                    <div className="flex flex-col items-center justify-center h-full bg-gray-50 dark:bg-gray-800">
                      <LogoSpinner size="large" />
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">{t('Processing document...')}</p>
                    </div>
                  ) : documentHtml ? (
                    <div ref={documentContentRef} className="p-4 overflow-y-auto dark:text-gray-200" style={citation.country?.toUpperCase() === 'SE' ? { fontSize: '1.1em' } : undefined} dangerouslySetInnerHTML={{ __html: documentHtml }} />
                  ) : isPdf ? (
                    <iframe
                      src={`${fileSource}#toolbar=0&navpanes=0&scrollbar=0${citation.country?.toUpperCase() === 'SE' ? '&zoom=100' : ''}`}
                      className="w-full h-full border-none custom-scrollbar-main"
                      title={citation.title || citation.name}
                    />
                  ) : documentType === 'word' ? (
                    <div className="flex flex-col items-center justify-center h-full bg-gray-50 dark:bg-gray-800">
                      <LogoSpinner size="large" />
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">{t('Processing document...')}</p>
                    </div>
                  ) : ['excel', 'powerpoint'].includes(documentType) ? (
                    <iframe
                      src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileSource || '')}`}
                      className="w-full border-none h-[calc(100vh-80px)]"
                      title={citation.title || citation.name}
                    />
                  ) : (
                    <iframe
                      src={fileSource || ''}
                      className="w-full h-[calc(100vh-80px)] border-none"
                      title={citation.title || citation.name}
                    />
                  )}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center h-full text-center space-y-4"
              >
                <div className={`p-4 rounded-full ${typeConfig.bg}`}>
                  {typeConfig.icon}
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    {t('No content available')}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 max-w-md">
                    {t('This document may not have extractable content. Check the summary above or view the original document.')}
                  </p>
                </div>
                {citation.external_url && (
                  <Button
                    onClick={() => window.open(cleanExternalUrl(citation.external_url!), '_blank')}
                    className={`m-1`}
                    variant='outline'
                    size='small'
                  >
                    {t('View Original Document')}
                  </Button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      </div>
      <FeatureTour
        steps={sourceReaderTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default CustomFileReader;